import { Link as RouterLink } from 'react-router';

import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import Breadcrumbs from '../../components/Breadcrumbs/index.ts';
import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_DEBT_OVERVIEW } from './RRS.ts';

const linkInSubtitleClassName =
  'typography-v1-legacy-subtitle2 font-normal text-[#18A0FB]! no-underline hover:underline';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const DebtOverview: React.FC = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <Page title={appStrings.MENU.DEBT_OVERVIEW}>
      <RrsHandler {...RRS_DEBT_OVERVIEW} />
      <Section>
        <div className="flex max-w-[900px]! flex-col [&>button]:self-center">
          <Breadcrumbs
            className="mb-8"
            items={[
              { text: appStrings.MENU.HOME, url: routes.HOME },
              {
                text: appStrings.MENU.MY_ECONOMY,
                url: routes.MY_ECONOMY,
              },
              { text: appStrings.MENU.DEBT_OVERVIEW },
            ]}
          />
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Gjeldsoversikt – her finner du den gratis og enkelt
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Har du forbrukslån og kanskje et kredittkort eller tre? Hvis du i tillegg har lån til
            bolig, bil og andre verdier kan det fort bli vanskelig å få total gjeldsoversikt. Vi har
            gjort det enklere for deg å finne ut hvor mye du har i usikret gjeld – og hvor mye du
            kan spare på å refinansiere.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Vi vet som regel hvor mye vi skylder på boligen, bilen og studielånet. Men det er svært
            få av oss som har total gjeldsoversikt, så du er ikke alene dersom du ikke er 100
            prosent sikker på hvor mye du skylder til sammen. En av årsakene til dette er ofte at
            selv om du ikke har brukt noe på kredittkortene dine, så regnes maksbeløpet på kreditten
            med i den totale gjelden når bankene sjekker betalingsevnen din.
            <br />
            <br />
            Det betyr at hvis du skal søke om for eksempel boliglån, kan faktisk det å kvitte seg
            med ett eller flere kredittkort være det som skal til for at du får innvilget lånet,
            eller får lavere rente. Den enkleste måten å gjøre det på, er å ta kontakt med banken
            din for å få slettet kortet. Så snart de har gjort det, får{' '}
            <RouterLink to={routes.DEBT_REGISTER} className={linkInSubtitleClassName}>
              Gjeldsregisteret
            </RouterLink>{' '}
            beskjed – og dermed får du mindre i usikret gjeld.
          </p>
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Få oversikten over gjelden din
          </Button>
        </div>
      </Section>
      <Section background="paper" last>
        <div className="mx-auto flex w-full max-w-[900px]! flex-col sm:flex-row sm:pb-[3px]">
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Men, hva er Gjeldsregisteret?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Dette er en tjeneste som hjelper deg å få oversikt over den usikrede gjelden din, som
              kredittkort og forbrukslån. Ideen bak Gjeldsregisteret er at banker og andre långivere
              skal unngå å innvilge lån og kreditter til de som har for mye gjeld fra før.
              <br />
              <br />
              Fordelen er dermed at du ikke får mer i lån enn du klarer å betale tilbake. En annen
              fordel er at du får total gjeldsoversikt, selv om du altså ikke har brukt kredittene
              dine. Det er mange som har logget seg inn i Gjeldsregisteret, og funnet både kort og
              kjøpskreditter de hadde glemt. Så kanskje dette kan være et godt tidspunkt å få ryddet
              opp i økonomien din?
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvordan finner jeg den totale gjeldsoversikten min?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              <RouterLink to={routes.DEBT_REGISTER} className={linkClassName}>
                Det gjør du gratis og enkelt hos Scoopr
              </RouterLink>
              , og via Gjeldsregisteret får du fullt innsyn i den usikrede gjelden din. Du logger
              inn via ID-porten, hvilket gjør det 100 prosent trygt. Hvis du har BankID på mobilen,
              sjekker du gjelden din like enkelt som du logger inn i nettbanken.
              <br />
              <br />
              Hos Gjeldsregisteret ser du alle kreditter og lån, og i tillegg ser du rentene. Du
              synes kanskje de er høye? I så fall bør du absolutt vurdere å refinansiere – det kan
              du spare mange tusen kroner i måneden på.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Refinansier kredittkort og forbrukslån hos Scoopr
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Uansett om du gjør den allerede nevnte slettingen av kredittkortene dine, må du
              selvsagt betale det du skylder på dem. Men hva gjør du dersom du skylder mer enn du
              klarer å betale her og nå, og kanskje i tillegg har forbrukslån?{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                Da søker du om å refinansiere
              </RouterLink>{' '}
              – altså samler alle den gamle usikrede gjelden din i ett nytt lån.
              <br />
              <br />
              Hos Scoopr tar det deg kun to minutter å sjekke om du kan{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                få lavere rente på forbrukslån og kredittkort
              </RouterLink>
              . Det er helt uforpliktende og gratis, og du kan søke om refinansiering fra 20.000 til
              500.000 kroner. Søknaden din går automatisk ut til 13 banker, som gjør alt de kan for
              å gi deg lavest mulig rente. Du har med andre ord alt å vinne på å bruke Scoopr når du
              skal søke om refinansiering.
              <br />
              <br />
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                Se hvor mye du kan spare når du refinansierer hos Scoopr
              </RouterLink>
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center [&>button]:self-center">
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Få oversikten over gjelden din
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default DebtOverview;
